import type { SelectOption } from "./selectModel";

/**
 * 열려 있는 작업자 고르개와 판의 이름 열을 잇는 자리.
 *
 * 고르개가 열려 있는 동안에는 판의 이름이 곧 목록의 한 줄이다 — 이름을 눌러 고를 수 있으면
 * 드랍다운을 뒤질 일이 없다. 열린 고르개는 언제나 하나뿐이라 "어디로 들어가는가"가 애매하지
 * 않고, 그래서 불출·수동 배정·회수·검수가 한 규칙으로 덮인다.
 *
 * 회수·검수의 목록은 사람이 아니라 불출 건이라 한 사람에게 여럿일 수 있다. 그때는 고르지
 * 않고 목록을 그 사람 것만 남긴다 — 임의로 하나를 집으면 어느 건이 들어갔는지 모른 채
 * 넘어가고, 그건 수량이 걸린 기록에서 가장 위험한 실수다.
 */
type Session = {
  options: SelectOption[];
  choose: (option: SelectOption) => void;
  narrow: (query: string) => void;
};

let session: Session | null = null;

export function beginWorkerPick(next: Session) {
  session = next;
  document.documentElement.dataset.workerPick = "";
  return () => {
    if (session !== next) return;
    session = null;
    delete document.documentElement.dataset.workerPick;
  };
}

/** 판의 이름 열이 지금 고를 수 있는 상태인가. */
export const workerPickOpen = () => !!session;

/**
 * 판에서 누른 사람을 열린 고르개에 넣는다. 넣었거나 목록을 좁혔으면 true —
 * 그때는 부른 쪽이 제 일(사람 고르기)을 하지 않고 비켜야 한다.
 */
export function pickWorker(workerId: string, name: string) {
  if (!session) return false;
  const usable = session.options.filter(option => !option.disabled && option.value !== "");
  const exact = usable.find(option => option.value === workerId);
  if (exact) { session.choose(exact); return true; }
  // Return/inspection options begin with the displayed worker name. A substring
  // match could select 강민지's issuance when the board header for 강민 is clicked.
  const matches = usable.filter(option => option.label.split(" · ")[0].trim() === name);
  if (matches.length === 1) { session.choose(matches[0]); return true; }
  if (matches.length > 1) { session.narrow(name); return true; }
  return false;
}

/* 고르개가 닫혀 있을 때 판의 이름이 들어갈 자리 — 오른쪽 카드의 불출 줄(2026-11-03 지윤).
   이름을 눌렀는데 그 사람의 지금 블락이 끝나 있어 펼칠 칸이 없으면, 카드 불출 줄에 그 사람을 넣고 수량 칸으로 간다.
   받을 카드(골라 둔 하위공정)가 없으면 false. */
/* 카드(WorkManagement)는 판 안팎에 둘 이상 서 있을 수 있다 — 받을 수 있는 쪽 하나가 받는다. */
const idle = new Set<(workerId: string) => boolean>();

export function registerIdleWorkerPick(next: (workerId: string) => boolean) {
  idle.add(next);
  return () => { idle.delete(next); };
}

export const pickIdleWorker = (workerId: string) => [...idle].some(pick => pick(workerId));
